import {
    CalendarBlankIcon,
    ClockCountdownIcon,
    FileVideoIcon,
    MapPinIcon,
} from '@phosphor-icons/react';
import type { EventCardMetadataItem } from '@/components/EventCard/EventCard';
import {
    formatDate,
    formatDetectedAtTime,
    formatEventTimeWithDuration,
    formatRelativeTimeI18n,
    isVideoRangeEvent,
} from './date';
import { SourceType, type FullSource } from '@/types/source';
import type { Event } from '@/types/event';
import { SecurityCameraIconMirrored } from './icons';
import { getEventTimeRange } from './event-time-range';

interface EventCardMetadataParams {
    createdAt?: number;
    eventStartTime?: number;
    eventEndTime?: number;
    isVideoRange: boolean;
    sourceType?: SourceType;
    sourceLabel: string;
    locationName?: string;
}

function getSourceLabel(sourceLabel: string): string | undefined {
    const trimmedSourceLabel = sourceLabel.trim();
    return trimmedSourceLabel.length > 0 ? trimmedSourceLabel : undefined;
}

export function buildEventCardMetadata({
    createdAt,
    eventStartTime,
    eventEndTime,
    isVideoRange,
    sourceType,
    sourceLabel,
    locationName,
}: EventCardMetadataParams): EventCardMetadataItem[] {
    const timeText =
        isVideoRange && eventStartTime !== undefined
            ? eventEndTime !== undefined
                ? formatEventTimeWithDuration(eventStartTime, eventEndTime)
                : formatDetectedAtTime(eventStartTime)
            : createdAt
              ? formatRelativeTimeI18n(createdAt)
              : undefined;
    const resolvedSourceLabel = getSourceLabel(sourceLabel);

    return [
        ...(timeText
            ? [
                  {
                      icon: isVideoRange
                          ? ClockCountdownIcon
                          : CalendarBlankIcon,
                      text: timeText,
                      title: createdAt ? formatDate(createdAt) : undefined,
                  },
              ]
            : []),
        ...(resolvedSourceLabel
            ? [
                  {
                      icon:
                          sourceType === SourceType.CAMERA
                              ? SecurityCameraIconMirrored
                              : FileVideoIcon,
                      text: resolvedSourceLabel,
                  },
              ]
            : []),
        ...(locationName
            ? [
                  {
                      icon: MapPinIcon,
                      text: locationName,
                  },
              ]
            : []),
    ];
}

export function buildEventCardMetadataFromEvent(
    event: Event,
    source: FullSource | undefined,
): EventCardMetadataItem[] {
    const sourceType = event.sourceType ?? source?.type;
    const { startTime: eventStartTime, endTime: eventEndTime } =
        getEventTimeRange(event);
    const isVideoRange = isVideoRangeEvent(
        eventStartTime,
        eventEndTime,
        sourceType,
    );
    return buildEventCardMetadata({
        createdAt: event.createdAt,
        eventStartTime,
        eventEndTime,
        isVideoRange,
        sourceType,
        sourceLabel: event.sourceName ?? source?.displayName ?? '',
        locationName: source?.location?.name,
    });
}
